import { Timeframe, Timeperiod } from 'interfaces/chart'
import * as moment from 'moment'
import { DATE_FORMAT_US, DATE_FORMAT_ISO_FULL, DATE_FORMAT_ISO_DAYLESS, DATE_FORMAT_READABLE, DATE_FORMAT_READABLE_YEARLESS } from 'constants/datetime'


export function getMonthsCount (timeframe: Timeframe): number {
  switch (timeframe) {
    case '13-months': return 13
    case '6-months': return 6
    default: return 1
  }
}

export const isTimeframeWithToday = (timeframe: Timeframe): boolean =>
  timeframe !== 'last-month'

export const formatUSDate = (date: moment.Moment | string, format: string = DATE_FORMAT_US): string =>
  moment(date).format(format)

type DateFallback = 'year' | 'week'
/**
 * Returns `Today`, `Yesterday` or in human date format
 * @param strDate
 * @returns {string}
 */
export function formatRelativeDate (strDate: string, format: string = DATE_FORMAT_READABLE, fallback: DateFallback = 'year') {
  const date = moment(strDate)
  const now = new Date()
  const rawDate = date.format(DATE_FORMAT_ISO_FULL)

  const today = moment().format(DATE_FORMAT_ISO_FULL)
  if (rawDate === today) {
    return `Today`
  }

  const yesterday = moment().subtract(1, 'day').format(DATE_FORMAT_ISO_FULL)
  if (rawDate === yesterday) {
    return `Yesterday`
  }

  const twoDaysAgo = moment().subtract(2, 'day').format(DATE_FORMAT_ISO_FULL)
  if (rawDate === twoDaysAgo) {
    return `Two days ago`
  }

  const tomorrow = moment().add(1, 'day').format(DATE_FORMAT_ISO_FULL)
  if (rawDate === tomorrow) {
    return `Tomorrow`
  }

  const twoDaysFromNow = moment().add(2, 'day').format(DATE_FORMAT_ISO_FULL)
  if (rawDate === twoDaysFromNow) {
    return `In two days`
  }

  const isCurrentWeek = date.isSame(now, 'week')
  if (isCurrentWeek || fallback === 'week') {
    return date.format('dddd')
  }

  const isCurrentYear = date.isSame(now, 'year')
  if (isCurrentYear) {
    return date.format(DATE_FORMAT_READABLE_YEARLESS)
  }

  return date.format(format)
}

export function createTimeperiod (timeframe: Timeframe): Timeperiod {
  const lastDate = timeframe === 'last-month' ?
    moment().subtract(1, 'month') :
    moment()

  const monthAmount = getMonthsCount(timeframe)
  const startDate = moment(lastDate).subtract(monthAmount - 1, 'months')

  const timeperiod = {
    start_date: startDate.format(DATE_FORMAT_ISO_DAYLESS),
    end_date: lastDate.format(DATE_FORMAT_ISO_DAYLESS),
  }

  return timeperiod
}

export const getTimeframeLastMonth = (timeframe: Timeframe): string =>
  createTimeperiod(timeframe).end_date

// check if raw timeperiod can be correct timeframe
// f.e. for insights cta filters
export const canBeTimeframe = ({ start_date, end_date }: Timeperiod): boolean => {
  const currentMonth = moment().format(DATE_FORMAT_ISO_DAYLESS)
  const isOneMonth = start_date === end_date

  if (isOneMonth) {
    // can be current month or last month
    const lastMonth = moment().subtract(1, 'month').format(DATE_FORMAT_ISO_DAYLESS)
    return currentMonth === start_date || lastMonth === start_date
  }

  // can't be 6-months or 13-months
  if (end_date !== currentMonth) {
    return false
  }

  const sixMonthsStartDate = moment(currentMonth).subtract(5, 'months').format(DATE_FORMAT_ISO_DAYLESS)
  const thirteenMonthsStartDate = moment(currentMonth).subtract(12, 'months').format(DATE_FORMAT_ISO_DAYLESS)

  // only 6-months or 13-months
  return sixMonthsStartDate === start_date || thirteenMonthsStartDate === start_date
}


export const timeperiodToTimeframe = ({ start_date, end_date }: Timeperiod): Timeframe => {
  const currentMonth = moment().format(DATE_FORMAT_ISO_DAYLESS)

  const isOneMonth = start_date === end_date
  if (isOneMonth) {
    return start_date === currentMonth ?
      'current-month' :
      'last-month'
  }

  const isSixMonths = moment(end_date).subtract(5, 'months').format(DATE_FORMAT_ISO_DAYLESS) === start_date
  return isSixMonths ?
    '6-months' :
    '13-months'
}

export const postDate = (date: string) => moment(date).fromNow()

export const createGetAgeMonthPrecision = (birthDate: string) => {
  const monthOfBirth = moment(birthDate).startOf('month')
  return (date: string) => {
    return moment(date).startOf('month').diff(monthOfBirth, 'years')
  }
}

export const monthDiff = (d1: Date, d2: Date) => {
  let months
  months = (d2.getFullYear() - d1.getFullYear()) * 12
  months -= d1.getMonth()
  months += d2.getMonth()
  return months <= 0 ? 0 : months
}

export const monthsLeftInYear = () => {
  const lastDateOfYear = new Date(new Date().getFullYear(), 11, 31)
  return monthDiff(new Date(), lastDateOfYear)
}
